import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemFieldsAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';

import type { parentIssueField_updateWorkItemFieldsActionRenderer$key } from './__generated__/parentIssueField_updateWorkItemFieldsActionRenderer.graphql';
import { ParentIssueFieldSkeleton } from './ParentIssueFieldSkeleton';
import { useHydratedIssue } from './useHydratedIssue';

const messages = defineMessages({
	addToParentWorkItem: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.parent-issue-field.addToParentWorkItem',
		defaultMessage: 'Add to parent work item',
		description:
			'Text shown on the button or option to assign the current work item to a parent work item in a Jira action card.',
	},
	unassign: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.parent-issue-field.unassign',
		defaultMessage: 'Unassign parent',
		description:
			'Text shown on the button or option to remove the parent work item assignment from the current Jira work item in an action card.',
	},
});

export type Props = {
	cloudId: string;
	queryRef: parentIssueField_updateWorkItemFieldsActionRenderer$key;
	action: UpdateWorkItemFieldsAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const ParentIssueField = ({
	cloudId,
	queryRef,
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	di(useHydratedIssue);

	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment parentIssueField_updateWorkItemFieldsActionRenderer on JiraParentIssueField {
				fieldId
				name
				parentIssue {
					issueId
					key
					summary
				}
			}
		`,
		queryRef,
	);

	const proposedParentId =
		typeof action?.data?.fieldValue === 'string'
			? action.data.fieldValue.trim()
			: action?.data?.fieldValue?.toString() || null;

	const isFieldRenderable = data?.fieldId === 'parent' && data.fieldId === action?.data?.fieldKey;

	const needsHydration = Boolean(
		isFieldRenderable && proposedParentId && proposedParentId !== data?.parentIssue?.issueId,
	);

	const hydratedData = useHydratedIssue(cloudId, proposedParentId, needsHydration);

	const parentIssue =
		needsHydration && hydratedData
			? { issue: hydratedData.issue, isLoading: hydratedData.isLoading, error: hydratedData.error }
			: { issue: data?.parentIssue, isLoading: false, error: null };

	const { issue, isLoading, error } = parentIssue;

	const isAssignable = (issue || isLoading) && proposedParentId;
	const isNotFound = !issue && !isLoading && proposedParentId;

	const formattedMessage = isAssignable
		? formatMessage(messages.addToParentWorkItem, {
				value: isLoading ? '' : `${issue?.key}: ${issue?.summary}`,
			})
		: formatMessage(messages.unassign);

	// Compute tooltip content provider logic
	const toolTipContentProvider = useCallback(() => {
		if (!isFieldRenderable) {
			return null;
		}
		return (
			<FieldTooltipContent
				reasoning={action?.data?.reasoning}
				current={
					data?.parentIssue ? `${data.parentIssue.key}: ${data.parentIssue.summary}` : undefined
				}
				suggested={issue ? `${issue?.key}: ${issue?.summary}` : undefined}
				fieldName={data?.name}
			/>
		);
	}, [action?.data?.reasoning, data.parentIssue, data?.name, isFieldRenderable, issue]);

	// Register tooltip content provider
	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: action?.data?.fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	if (!isFieldRenderable || isNotFound || error) {
		return null;
	}

	if (isLoading) {
		return <ParentIssueFieldSkeleton />;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-fields-action-renderer.ui.action-card.card-fields-list.parent-issue-field"
		>
			{formattedMessage}
		</Text>
	);
};
